(() => {
  const sectionState = new WeakMap();

  const safeJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent || "null");
    } catch (error) {
      return null;
    }
  };

  const normalizeRowCount = (value) => {
    const digits = String(value || "").match(/\d+/);
    const parsed = digits ? Number(digits[0]) : 5;
    if (parsed > 8) return 8;
    if (parsed < 2) return 2;
    return parsed;
  };

  const GALLERY_SIZES = "(min-width: 990px) 46vw, 100vw";
  const preloadedSrcs = new Set();

  const preloadImage = (media) => {
    if (!media?.src || preloadedSrcs.has(media.src)) return;
    preloadedSrcs.add(media.src);
    const image = new Image();
    if (media.srcset) image.srcset = media.srcset;
    image.sizes = GALLERY_SIZES;
    image.src = media.src;
  };

  const applyMainImage = (mainImage, media) => {
    if (media.srcset) mainImage.srcset = media.srcset;
    if (!mainImage.getAttribute("sizes")) mainImage.setAttribute("sizes", GALLERY_SIZES);
    if (media.src) mainImage.src = media.src;
    if (media.alt) mainImage.alt = media.alt;
  };

  const switchMainImage = (section, media) => {
    if (!media) return;
    const mainImage = section.querySelector(".sk24-product__main-image");
    if (!mainImage) return;

    section.querySelectorAll("[data-sk24-thumb]").forEach((thumb) => {
      const isActive = media.id && String(thumb.dataset.mediaId) === String(media.id);
      thumb.classList.toggle("is-active", isActive);
      thumb.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    if (!media.src) return;
    if (mainImage.src === media.src && (!media.srcset || mainImage.srcset === media.srcset)) return;

    const requestKey = String(media.id || media.src);
    mainImage.dataset.pendingMedia = requestKey;

    const loader = new Image();
    if (media.srcset) loader.srcset = media.srcset;
    loader.sizes = mainImage.getAttribute("sizes") || GALLERY_SIZES;
    const reveal = () => {
      if (mainImage.dataset.pendingMedia !== requestKey) return;
      applyMainImage(mainImage, media);
    };
    loader.addEventListener("load", reveal, { once: true });
    loader.addEventListener("error", reveal, { once: true });
    loader.src = media.src;
    if (loader.complete) reveal();
  };

  const updateDrawingRows = (section, selectedValue) => {
    const drawing = section.querySelector("[data-sk24-drawing]");
    if (!drawing) return;
    const rowCount = normalizeRowCount(selectedValue);
    drawing.setAttribute("data-rows", String(rowCount));
    drawing.querySelectorAll("[data-row-index]").forEach((rowNode) => {
      const index = Number(rowNode.getAttribute("data-row-index"));
      rowNode.classList.toggle("is-hidden", index > rowCount);
    });
  };

  const updateDrawingFromVariant = (section, variant) => {
    const rowFieldset = Array.from(section.querySelectorAll("[data-option-name]")).find((fieldset) =>
      (fieldset.dataset.optionName || "").toLowerCase().includes("reihe")
    );
    if (!rowFieldset || !variant) return;

    const rowOptionName = rowFieldset.dataset.optionName;
    const optionIndex = Array.from(section.querySelectorAll("variant-selects fieldset")).indexOf(rowFieldset);
    const optionKey = optionIndex === 0 ? "option1" : optionIndex === 1 ? "option2" : "option3";
    const rowValue = variant[optionKey];
    if (rowValue) updateDrawingRows(section, rowValue);
  };

  const applyVariantMedia = (section, state, variant) => {
    if (!variant) return;

    if (variant.featured_media?.id) {
      const media = state.media.find((entry) => String(entry.id) === String(variant.featured_media.id));
      if (media) switchMainImage(section, media);
      return;
    }

    if (variant.featured_image?.src) {
      switchMainImage(section, {
        id: "",
        src: variant.featured_image.src,
        srcset: "",
        alt: variant.featured_image.alt || "",
      });
    }
  };

  const initVariantChangeHandler = (section, state) => {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;

    const productInfo = section.querySelector("product-info");
    const sectionId = productInfo?.dataset.section;
    if (!sectionId) return;

    const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const { sectionId: eventSectionId, variant } = event?.data || {};
      if (String(eventSectionId) !== String(sectionId)) return;

      applyVariantMedia(section, state, variant);
      updateDrawingFromVariant(section, variant);
    });

    state.variantChangeUnsubscriber = unsubscribe;
  };

  const initTabs = (section, signal) => {
    const tabs = Array.from(section.querySelectorAll("[data-tab-target]"));
    const panels = Array.from(section.querySelectorAll("[data-tab-panel]"));
    if (!tabs.length || !panels.length) return;

    const setActiveTab = (tabName) => {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.tabTarget === tabName;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });
      panels.forEach((panel) => {
        const isActive = panel.dataset.tabPanel === tabName;
        panel.hidden = !isActive;
        panel.classList.toggle("is-active", isActive);
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener(
        "click",
        () => {
          setActiveTab(tab.dataset.tabTarget);
        },
        { signal }
      );

      tab.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
          event.preventDefault();
          const dir = event.key === "ArrowRight" ? 1 : -1;
          const nextIndex = (index + dir + tabs.length) % tabs.length;
          tabs[nextIndex].focus();
          setActiveTab(tabs[nextIndex].dataset.tabTarget);
        },
        { signal }
      );
    });
  };

  const mediaFromThumb = (thumb) => ({
    id: thumb.dataset.mediaId,
    src: thumb.dataset.mediaSrc,
    srcset: thumb.dataset.mediaSrcset,
    alt: thumb.dataset.mediaAlt,
  });

  const preloadGallery = (section, state) => {
    const warm = () => {
      (state.media || []).forEach(preloadImage);
      section.querySelectorAll("[data-sk24-thumb]").forEach((thumb) => preloadImage(mediaFromThumb(thumb)));
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(warm, { timeout: 400 });
      return;
    }
    window.setTimeout(warm, 200);
  };

  const initGallery = (section, signal) => {
    const thumbs = section.querySelectorAll("[data-sk24-thumb]");
    thumbs.forEach((thumb) => {
      const media = mediaFromThumb(thumb);
      const warm = () => preloadImage(media);
      thumb.addEventListener("pointerenter", warm, { signal, once: true });
      thumb.addEventListener("focus", warm, { signal, once: true });
      thumb.addEventListener(
        "click",
        () => {
          switchMainImage(section, media);
        },
        { signal }
      );
    });
  };

  const initQtyStepper = (section, signal) => {
    const qtyInput = section.querySelector(".sk24-product__qty-input");
    if (!qtyInput) return;

    section.querySelectorAll("[data-qty-action]").forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          const current = Number(qtyInput.value) || 1;
          const min = Number(qtyInput.min) || 1;
          if (button.dataset.qtyAction === "minus") {
            qtyInput.value = String(Math.max(min, current - 1));
          } else {
            qtyInput.value = String(current + 1);
          }
        },
        { signal }
      );
    });
  };

  const getCartElement = () => document.querySelector("cart-drawer") || document.querySelector("cart-notification");

  const stromlaufplanIsInCart = async (card) => {
    const cartUrl = window.routes?.cart_url || "/cart";
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: "application/json" } });
    if (!response.ok) return false;
    const cart = await response.json();
    const parentId = String(card.dataset.parentId || "");
    const productId = String(card.dataset.productId || "");
    return (cart.items || []).some((item) => {
      const properties = item.properties || {};
      return String(item.product_id) === productId && String(properties._stromlaufplan_for || "") === parentId;
    });
  };

  const setStromlaufplanState = (section, added) => {
    const card = section.querySelector("[data-sk24-stromlaufplan]");
    const button = section.querySelector("[data-sk24-stromlaufplan-add]");
    if (!card || !button) return;
    const label = button.querySelector("[data-sk24-stromlaufplan-label]");
    const unavailable = button.dataset.unavailable === "true";
    card.classList.toggle("is-added", added);
    button.classList.toggle("is-added", added);
    button.disabled = added || unavailable;
    button.setAttribute("aria-pressed", added ? "true" : "false");
    if (label && !unavailable) {
      label.textContent = added ? button.dataset.addedLabel || "Im Warenkorb" : button.dataset.addLabel || "Hinzufügen";
    }
  };

  const addStromlaufplanToCart = async (section, button) => {
    const card = section.querySelector("[data-sk24-stromlaufplan]");
    if (!card || !button || button.disabled) return;

    const variantId = Number(card.dataset.variantId);
    if (!variantId) return;

    button.classList.add("loading");
    button.setAttribute("aria-disabled", "true");
    const spinner = button.querySelector(".loading__spinner");
    if (spinner) spinner.classList.remove("hidden");

    try {
      if (await stromlaufplanIsInCart(card)) {
        setStromlaufplanState(section, true);
        const cart = getCartElement();
        if (cart?.open) cart.open(button);
        return;
      }

      const payload = {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _stromlaufplan_for: String(card.dataset.parentId || ""),
              Für: card.dataset.parentTitle || "",
            },
          },
        ],
      };

      const cart = getCartElement();
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((entry) => entry.id);
        payload.sections_url = window.location.pathname;
      }

      const addUrl = window.routes?.cart_add_url || "/cart/add";
      const response = await fetch(addUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message || "Der Stromlaufplan konnte nicht hinzugefügt werden.");
      }

      setStromlaufplanState(section, true);

      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "sk24-stromlaufplan",
          productVariantId: variantId,
          cartData: data,
        });
      }

      if (cart) {
        if (data.sections && typeof cart.renderContents === "function") {
          cart.renderContents(data);
        }
        if (typeof cart.open === "function") cart.open(button);
      }
    } catch (error) {
      console.error(error);
    } finally {
      button.classList.remove("loading");
      button.removeAttribute("aria-disabled");
      if (spinner) spinner.classList.add("hidden");
    }
  };

  const initStromlaufplan = (section, state, signal) => {
    const card = section.querySelector("[data-sk24-stromlaufplan]");
    if (!card) return;

    section.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("[data-sk24-stromlaufplan-add]");
        if (!button || !section.contains(button)) return;
        event.preventDefault();
        addStromlaufplanToCart(section, button);
      },
      { signal }
    );

    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;

    const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event?.source === "sk24-stromlaufplan") return;
      try {
        setStromlaufplanState(section, await stromlaufplanIsInCart(card));
      } catch (error) {
        /* ignore cart sync errors */
      }
    });
    state.stromlaufplanUnsubscriber = unsubscribe;
  };

  const initAddToCartState = (section, signal) => {
    const form = section.querySelector(".sk24-product__form");
    const button = section.querySelector(".sk24-product__add-btn");
    if (!form || !button) return;

    form.addEventListener(
      "submit",
      () => {
        if (button.disabled) return;
        const addedLabel = button.dataset.addedLabel || "In den Warenkorb!";
        const defaultLabel = button.dataset.addLabel || "In den Warenkorb";
        const span = button.querySelector("span");
        button.classList.add("is-added");
        if (span) span.textContent = addedLabel;
        window.setTimeout(() => {
          button.classList.remove("is-added");
          if (span && !button.disabled) span.textContent = defaultLabel;
        }, 1500);
      },
      { signal }
    );
  };

  const initSection = (section) => {
    if (!section || sectionState.has(section)) return;

    const media = safeJSON(section.querySelector("[data-sk24-media]")) || [];
    const controller = new AbortController();
    const state = {
      media,
      controller,
      variantChangeUnsubscriber: undefined,
      stromlaufplanUnsubscriber: undefined,
    };
    sectionState.set(section, state);

    initTabs(section, controller.signal);
    initGallery(section, controller.signal);
    preloadGallery(section, state);
    initQtyStepper(section, controller.signal);
    initVariantChangeHandler(section, state);
    initAddToCartState(section, controller.signal);
    initStromlaufplan(section, state, controller.signal);

    const productInfo = section.querySelector("product-info");
    const selectedVariantScript = productInfo?.querySelector("variant-selects [data-selected-variant]");
    if (selectedVariantScript) {
      try {
        const variant = JSON.parse(selectedVariantScript.textContent);
        applyVariantMedia(section, state, variant);
        updateDrawingFromVariant(section, variant);
      } catch (error) {
        /* ignore invalid JSON */
      }
    }
  };

  const destroySection = (section) => {
    const state = sectionState.get(section);
    if (!state) return;
    state.variantChangeUnsubscriber?.();
    state.stromlaufplanUnsubscriber?.();
    state.controller.abort();
    sectionState.delete(section);
  };

  document.querySelectorAll(".sk24-product").forEach(initSection);

  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target.querySelector(".sk24-product");
    if (section) initSection(section);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (event.target.classList?.contains("sk24-product")) {
      destroySection(event.target);
      return;
    }
    const section = event.target.querySelector(".sk24-product");
    if (section) destroySection(section);
  });
})();
